/**
 * Centralized Language Configuration
 *
 * This is the SINGLE SOURCE OF TRUTH for all language definitions in the frontend.
 * To add a new language, add an entry to SUPPORTED_LANGUAGES below.
 *
 * All other files should import from this config instead of defining their own language lists.
 */

// =============================================================================
// LANGUAGE DEFINITIONS
// =============================================================================

/**
 * Master list of all supported languages
 *
 * Each language entry contains:
 * - code: Primary identifier used throughout the app (ISO 639-1 or BCP47)
 * - baseCode: ISO 639-1 code for translation services
 * - name: English display name
 * - nativeName: Name in the language itself
 * - flag: Emoji flag for UI display
 * - speechRecognition: BCP47 code for Web Speech API / Google Speech-to-Text
 * - enabled: Whether the language is currently available
 * - canBeNative: Can be selected as user's native/interface language
 * - canBeLearning: Can be selected as a language to learn
 */
export const SUPPORTED_LANGUAGES = {
  // English
  'en': {
    code: 'en',
    baseCode: 'en',
    name: 'English',
    nativeName: 'English',
    flag: '🇺🇸',
    speechRecognition: 'en-US',
    enabled: true,
    canBeNative: true,
    canBeLearning: true,
  },

  // French (France)
  'fr': {
    code: 'fr',
    baseCode: 'fr',
    name: 'French',
    nativeName: 'Français',
    flag: '🇫🇷',
    speechRecognition: 'fr-FR',
    enabled: true,
    canBeNative: true,
    canBeLearning: true,
  },

  // Spanish (Spain) - European Spanish
  'es': {
    code: 'es',
    baseCode: 'es',
    name: 'Spanish (Spain)',
    nativeName: 'Español (España)',
    flag: '🇪🇸',
    speechRecognition: 'es-ES',
    enabled: true,
    canBeNative: true,
    canBeLearning: true,
  },

  // Spanish (Latin America)
  'es-419': {
    code: 'es-419',
    baseCode: 'es',
    name: 'Spanish (Latin America)',
    nativeName: 'Español (Latinoamérica)',
    flag: '🇲🇽',
    speechRecognition: 'es-MX',
    enabled: true,
    canBeNative: true,
    canBeLearning: true,
  },

  // German
  'de': {
    code: 'de',
    baseCode: 'de',
    name: 'German',
    nativeName: 'Deutsch',
    flag: '🇩🇪',
    speechRecognition: 'de-DE',
    enabled: true,
    canBeNative: true,
    canBeLearning: true,
  },

  // Italian
  'it': {
    code: 'it',
    baseCode: 'it',
    name: 'Italian',
    nativeName: 'Italiano',
    flag: '🇮🇹',
    speechRecognition: 'it-IT',
    enabled: true,
    canBeNative: true,
    canBeLearning: true,
  },

  // Portuguese (Portugal)
  'pt': {
    code: 'pt',
    baseCode: 'pt',
    name: 'Portuguese (Portugal)',
    nativeName: 'Português (Portugal)',
    flag: '🇵🇹',
    speechRecognition: 'pt-PT',
    enabled: true,
    canBeNative: true,
    canBeLearning: true,
  },

  // Portuguese (Brazil)
  'pt-BR': {
    code: 'pt-BR',
    baseCode: 'pt',
    name: 'Portuguese (Brazil)',
    nativeName: 'Português (Brasil)',
    flag: '🇧🇷',
    speechRecognition: 'pt-BR',
    enabled: true,
    canBeNative: true,
    canBeLearning: true,
  },

  // Romanian
  'ro': {
    code: 'ro',
    baseCode: 'ro',
    name: 'Romanian',
    nativeName: 'Română',
    flag: '🇷🇴',
    speechRecognition: 'ro-RO',
    enabled: true,
    canBeNative: true,
    canBeLearning: true,
  },

  // Japanese
  'ja': {
    code: 'ja',
    baseCode: 'ja',
    name: 'Japanese',
    nativeName: '日本語',
    flag: '🇯🇵',
    speechRecognition: 'ja-JP',
    enabled: true,
    canBeNative: true,
    canBeLearning: true,
  },
};


// =============================================================================
// UI TRANSLATIONS
// =============================================================================

/**
 * UI string translations for interface localization
 * Add translations here when a language can be used as a native/interface language
 */
export const UI_TRANSLATIONS = {
  en: {
    home: "Home",
    explore: "Explore",
    stories: "Stories",
    journal: "Journal",
    vocabulary: "Vocabulary",
    history: "History",
    profile: "Profile",
    settings: "Settings",
    logout: "Logout",
    language: "Language",
    nativeLanguage: "Interface Language",
    learningLanguage: "Learning Language",
    journalTitle: "Language Journal",
    newEntry: "New Entry",
    writePlaceholder: "Start writing...",
    review: "Review",
    save: "Save",
    analyzing: "Analyzing...",
    tutorName: "AI Tutor",
    askQuestion: "Ask a question...",
    loading: "Loading..."
  },
  es: {
    home: "Inicio",
    explore: "Explorar",
    stories: "Historias",
    journal: "Diario",
    vocabulary: "Vocabulario",
    history: "Historial",
    profile: "Perfil",
    settings: "Configuración",
    logout: "Cerrar Sesión",
    language: "Idioma",
    nativeLanguage: "Idioma de Interfaz",
    learningLanguage: "Idioma de Aprendizaje",
    journalTitle: "Diario de Idiomas",
    newEntry: "Nueva Entrada",
    writePlaceholder: "Empieza a escribir...",
    review: "Revisar",
    save: "Guardar",
    analyzing: "Analizando...",
    tutorName: "Tutor IA",
    askQuestion: "Haz una pregunta...",
    loading: "Cargando..."
  },
  // Spanish Latin America uses same translations as Spanish Spain
  'es-419': {
    home: "Inicio",
    explore: "Explorar",
    stories: "Historias",
    journal: "Diario",
    vocabulary: "Vocabulario",
    history: "Historial",
    profile: "Perfil",
    settings: "Configuración",
    logout: "Cerrar Sesión",
    language: "Idioma",
    nativeLanguage: "Idioma de Interfaz",
    learningLanguage: "Idioma de Aprendizaje",
    journalTitle: "Diario de Idiomas",
    newEntry: "Nueva Entrada",
    writePlaceholder: "Empieza a escribir...",
    review: "Revisar",
    save: "Guardar",
    analyzing: "Analizando...",
    tutorName: "Tutor IA",
    askQuestion: "Haz una pregunta...",
    loading: "Cargando..."
  },
  fr: {
    home: "Accueil",
    explore: "Explorer",
    stories: "Histoires",
    journal: "Journal",
    vocabulary: "Vocabulaire",
    history: "Historique",
    profile: "Profil",
    settings: "Paramètres",
    logout: "Déconnexion",
    language: "Langue",
    nativeLanguage: "Langue d'interface",
    learningLanguage: "Langue d'apprentissage",
    journalTitle: "Journal Linguistique",
    newEntry: "Nouvelle Entrée",
    writePlaceholder: "Commencez à écrire...",
    review: "Réviser",
    save: "Enregistrer",
    analyzing: "Analyse en cours...",
    tutorName: "Tuteur IA",
    askQuestion: "Posez une question...",
    loading: "Chargement..."
  }
};


// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get all enabled languages as an array for dropdowns
 * @param {Object} options - Filter options
 * @param {boolean} options.nativeOnly - Only return languages that can be native
 * @param {boolean} options.learningOnly - Only return languages that can be learned
 * @returns {Array} Array of language objects with code and name
 */
export function getLanguageOptions({ nativeOnly = false, learningOnly = false } = {}) {
  return Object.values(SUPPORTED_LANGUAGES)
    .filter(lang => lang.enabled)
    .filter(lang => !nativeOnly || lang.canBeNative)
    .filter(lang => !learningOnly || lang.canBeLearning)
    .map(lang => ({
      code: lang.code,
      name: lang.name,
      nativeName: lang.nativeName,
      flag: lang.flag
    }));
}

/**
 * Get a language by its code
 * @param {string} code - Language code
 * @returns {Object|null} Language object or null if not found
 */
export function getLanguageByCode(code) {
  return SUPPORTED_LANGUAGES[code] || null;
}

/**
 * Get the display name for a language code
 * @param {string} code - Language code
 * @returns {string} Display name or the code if not found
 */
export function getLanguageDisplayName(code) {
  const language = SUPPORTED_LANGUAGES[code];
  return language ? language.name : code;
}

/**
 * Get the speech recognition code for a language
 * @param {string} code - Language code (e.g., 'es', 'es-419', 'french')
 * @returns {string} BCP47 code for speech recognition (e.g., 'es-ES', 'es-MX')
 */
export function getSpeechRecognitionCode(code) {
  // Handle full language names (legacy support)
  const normalizedCode = code?.toLowerCase();

  // Check if it's a language name
  const nameToCode = {
    'english': 'en',
    'french': 'fr',
    'spanish': 'es',
    'german': 'de',
    'italian': 'it',
    'portuguese': 'pt',
    'romanian': 'ro',
    'japanese': 'ja'
  };

  const languageCode = nameToCode[normalizedCode] || code;
  const language = SUPPORTED_LANGUAGES[languageCode];

  return language?.speechRecognition || 'en-US';
}

/**
 * Get the base language code for translation services
 * @param {string} code - Language code (e.g., 'es-419')
 * @returns {string} Base ISO 639-1 code (e.g., 'es')
 */
export function getBaseLanguageCode(code) {
  const language = SUPPORTED_LANGUAGES[code];
  return language?.baseCode || code;
}

/**
 * Get UI translation function for a specific language
 * @param {string} languageCode - The language code for translations
 * @returns {Function} Translation function that takes a key and returns translated string
 */
export function getTranslationFunction(languageCode) {
  return (key) => {
    // Try the specific language code first (e.g., 'es-419')
    let translations = UI_TRANSLATIONS[languageCode];

    // Fall back to base code if not found (e.g., 'es-419' -> 'es')
    if (!translations) {
      const baseCode = getBaseLanguageCode(languageCode);
      translations = UI_TRANSLATIONS[baseCode];
    }

    // Fall back to English
    if (!translations) {
      translations = UI_TRANSLATIONS['en'];
    }

    return translations[key] || key;
  };
}

/**
 * Check if a language code is valid and enabled
 * @param {string} code - Language code to check
 * @returns {boolean} True if valid and enabled
 */
export function isValidLanguage(code) {
  const language = SUPPORTED_LANGUAGES[code];
  return language?.enabled || false;
}

/**
 * Get languages grouped by base language (for showing dialects together)
 * @returns {Object} Object with base codes as keys and arrays of dialects as values
 */
export function getLanguagesGroupedByBase() {
  const grouped = {};

  Object.values(SUPPORTED_LANGUAGES)
    .filter(lang => lang.enabled)
    .forEach(lang => {
      const base = lang.baseCode;
      if (!grouped[base]) {
        grouped[base] = [];
      }
      grouped[base].push(lang);
    });

  return grouped;
}


// =============================================================================
// LEGACY COMPATIBILITY
// =============================================================================

/**
 * Legacy format for backward compatibility with existing components
 * This matches the old { code, name } format used in RegisterPage, ProfilePage, etc.
 */
export const languageOptions = getLanguageOptions();

/**
 * Legacy translations export for backward compatibility with LanguageContext
 */
export const translations = UI_TRANSLATIONS;


// =============================================================================
// PROFICIENCY LEVELS (Not language-specific but commonly used with languages)
// =============================================================================

export const PROFICIENCY_LEVELS = [
  { value: 'A0', label: 'A0 (Absolute Beginner)' },
  { value: 'A1', label: 'A1 (Beginner)' },
  { value: 'A2', label: 'A2 (Elementary)' },
  { value: 'B1', label: 'B1 (Intermediate)' },
  { value: 'B2', label: 'B2 (Upper Intermediate)' },
  { value: 'C1', label: 'C1 (Advanced)' },
  { value: 'C2', label: 'C2 (Proficient)' }
];